import AvgContactAttemptsIcon from '@forwardflow/assets/svg/AvgContactAttemptsIcon'
import AvgSpeedToLeadIcon from '@forwardflow/assets/svg/AvgSpeedToLeadIcon'
import ConversionRateIcon from '@forwardflow/assets/svg/ConversionRateIcon'
import LeadsNotContactedIcon from '@forwardflow/assets/svg/LeadsNotContactedIcon'
import { useGetDashboardChartsOverview } from '@forwardflow/hooks/customHooks'
import { TimePeriod } from '@forwardflow/types'
import { Skeleton } from 'antd'
import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

const metricIcons: Record<string, JSX.Element> = {
   conversionRate: <ConversionRateIcon />,
   leadsNotContacted: <LeadsNotContactedIcon />,
   avgContactAttempts: <AvgContactAttemptsIcon />,
   avgSpeedToLead: <AvgSpeedToLeadIcon />,
}

export const DashboardChartsOverviewLoading: React.FC = () => (
   <div className="flex items-center justify-center py-10">
      <span className="p-4 text-lg text-gray-400">
         <Skeleton active paragraph={{ rows: 1 }} title={false} />
      </span>
   </div>
)

export const PerformanceAnalyticsCard: React.FC = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'

   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoadingFlag,
   } = useGetDashboardChartsOverview({
      timePeriod: timePeriod as TimePeriod,
   })

   const [activeTab, setActiveTab] = useState(0)

   const metrics =
      DashboardChartsOverview?.analytics?.tabs?.[activeTab]?.metrics ?? []

   const EmptyForwardFlowCard = () => (
      <div className="flex items-center w-full h-full px-4 py-2">
         <img
            src="./images/empty.png"
            alt="No Forward Flow Data"
            className="w-24 h-24 mr-5"
         />
         <div>
            <div className="text-sm text-white/70">
               No Forward Flow data available yet
            </div>
            <div className="mt-2 text-sm text-white/70">
               <Link to="/campaign" className="text-[#A78BFA] font-medium">
                  Deploy your first campaign
               </Link>{' '}
               to start tracking automated performance here.
            </div>
         </div>
      </div>
   )
   if (DashboardChartsOverviewLoadingFlag)
      return <DashboardChartsOverviewLoading />
   return (
      <div className="bg-white/5 rounded-xl">
         <div className="flex flex-col items-start gap-4 p-4 border-b-2 md:flex-row md:items-center md:justify-between md:px-6 md:py-0 h-14 border-black/50">
            <span className="font-semibold text-white md:text-lg">
               Performance Analytics
            </span>
            <div className="inline-flex w-auto overflow-x-auto rounded bg-black/40">
               {(DashboardChartsOverview?.analytics?.tabs ?? []).map(
                  (tab, idx) => (
                     <button
                        key={tab.id}
                        className={`px-3 md:px-4 py-1 font-medium transition whitespace-nowrap ${
                           activeTab === idx
                              ? 'bg-[#232228] text-white shadow'
                              : 'bg-transparent text-gray-400 hover:bg-[#232228]'
                        }`}
                        onClick={() => setActiveTab(idx)}
                     >
                        {tab.name}
                     </button>
                  )
               )}
            </div>
         </div>
         <div className="flex flex-col gap-4 xl:flex-row">
            {DashboardChartsOverviewLoadingFlag ? (
               <DashboardChartsOverviewLoading />
            ) : metrics.length === 0 ? (
               <EmptyForwardFlowCard />
            ) : (
               <div className="flex flex-col w-full gap-4 xl:flex-row">
                  {metrics.map((metric, idx) => (
                     <div
                        key={metric.id}
                        className={`flex-1 p-3 md:p-4 xl:px-6 xl:py-4 flex items-center gap-4 min-w-[220px] ${
                           idx !== metrics.length - 1
                              ? 'border-b-2 xl:border-b-0 xl:border-r-2 border-black/50'
                              : ''
                        }`}
                     >
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg md:w-16 md:h-16 bg-black/10 flexc">
                           {metricIcons[metric.id]}
                        </div>
                        <div>
                           <div className="flex flex-wrap items-center gap-2">
                              <span
                                 className={`text-lg md:text-xl font-semibold ${
                                    metric.isAlert
                                       ? 'text-pink-400'
                                       : 'text-white'
                                 }`}
                              >
                                 {metric.value?.toLocaleString()} {metric.unit}
                                 {metric.title && ` ${metric.title}`}
                              </span>
                           </div>
                           <div className="text-xs md:text-sm">
                              {metric.subtext}
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}
         </div>
      </div>
   )
}
